import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
    } catch (e) {
      console.error('Error fetching cart:', e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
    }
  }, [user, fetchCart]);

  const addToCart = async (productId, quantity = 1, size = null, color = null) => {
    if (!user) {
      return { success: false, error: 'Please login to add items to cart' };
    }

    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity, size, color }, { withCredentials: true });
      await fetchCart();
      setCartOpen(true);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to add item to cart' };
    }
  };

  const updateCartItem = async (itemIndex, quantity) => {
    try {
      await axios.put(`${API}/cart/${itemIndex}`, { quantity }, { withCredentials: true });
      await fetchCart();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to update cart' };
    }
  };

  const removeFromCart = async (itemIndex) => {
    try {
      await axios.delete(`${API}/cart/${itemIndex}`, { withCredentials: true });
      await fetchCart();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to remove item' };
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`, { withCredentials: true });
      setCart({ items: [], total: 0 });
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to clear cart' };
    }
  };

  const cartCount = cart.items.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <CartContext.Provider value={{ 
      cart, 
      loading, 
      cartOpen, 
      setCartOpen, 
      cartCount,
      addToCart, 
      updateCartItem, 
      removeFromCart, 
      clearCart,
      fetchCart 
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
